import { computed, inject, Injectable, signal } from '@angular/core'
import { StripeCheckoutDto } from '@mos/api/bank/dto-client'
import { signalPersistence } from '@mos/client/shared/storage/signal-persistence'
import { StorageStrategy } from '@mos/client/shared/storage/strategies/storage.strategy'

export interface TrackingProps {
	isConnected: boolean
	isPremium: boolean
	story: StoryProps | null
	pendingCheckout: Omit<StripeCheckoutDto, 'url'> | null
}

interface StoryProps {
	chapterNumber: number
	memoryNumber: number | null
	isInReplay: boolean
}

interface PersistedTrackingProps {
	pendingCheckout: Omit<StripeCheckoutDto, 'url'> | null
}

@Injectable({ providedIn: 'root' })
export class TrackingRepository {
	storageStrategy = inject(StorageStrategy)
	store = signal<TrackingProps>({
		isConnected: false,
		isPremium: false,
		story: null,
		pendingCheckout: null,
	})

	constructor() {
		signalPersistence<PersistedTrackingProps>({
			key: 'mos-tracking',
			storage: this.storageStrategy,
			read: () => ({ pendingCheckout: this.store().pendingCheckout }),
			write: (stored) => this.store.update((state) => ({ ...state, ...stored })),
		})
	}

	get state() {
		return this.store()
	}

	pendingCheckout = computed(() => this.store().pendingCheckout)

	set(isConnected: boolean, isPremium: boolean, story: StoryProps) {
		this.store.update((state) => ({ ...state, isConnected, isPremium, story }))
	}

	updateChapter(chapterNumber: number, isReplay: boolean) {
		this.store.update((state) => ({
			...state,
			story: {
				chapterNumber: chapterNumber,
				memoryNumber: state.story?.memoryNumber ?? null,
				isInReplay: isReplay,
			},
		}))
	}

	updateMemory(memoryNumber: number | null) {
		this.store.update((state) => ({
			...state,
			story: {
				memoryNumber: memoryNumber,
				chapterNumber: state.story?.chapterNumber ?? 0,
				isInReplay: state.story?.isInReplay ?? false,
			},
		}))
	}

	logout() {
		this.store.update((state) => ({ ...state, isConnected: false, story: null, isPremium: false }))
	}

	setPremium() {
		this.store.update((state) => ({ ...state, isPremium: true }))
	}

	setPendingCheckout(checkout: StripeCheckoutDto) {
		this.store.update((state) => ({ ...state, pendingCheckout: checkout }))
	}

	removePendingCheckout() {
		this.store.update((state) => ({ ...state, pendingCheckout: null }))
	}
}
